import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import type { UpdateOptions } from '../use-tab-search';
import type { ApiTriggeredSearch, NewslettersSearch } from '../url-state';
import { GoalFilter } from './list-filters/goal-filter';
import { ObjectTypeFilter, useHasObjectTypes } from './list-filters/object-type-filter';
import { TagFilter } from './list-filters/tag-filter';
import { TopicFilter, useHasSubscriptionTopics } from './list-filters/topic-filter';

/** The filters both tabs take from a workspace catalog. */
type CatalogSearch = Pick<
  NewslettersSearch & ApiTriggeredSearch,
  'tagIds' | 'goalIds' | 'objectTypeIds' | 'subscriptionTopicId'
>;

type CatalogFiltersProps = {
  search: CatalogSearch;
  onChange: (patch: Partial<CatalogSearch>, options: UpdateOptions) => void;
};

/**
 * Tags, goals and object types, in that order. Goals appear only with the `show-goals` flag, and
 * object types only in a workspace that has some. Picks replace the history entry, so Back
 * leaves the list rather than stepping through each one.
 */
export function CatalogFilters({ search, onChange }: CatalogFiltersProps) {
  const showGoals = useFeatureFlag(AccountFeatureFlag.ShowGoals);
  const hasObjectTypes = useHasObjectTypes();

  return (
    <>
      <TagFilter
        selectedTagIds={search.tagIds ?? []}
        onTagIdsChange={(tagIds) => onChange({ tagIds }, { replace: true })}
      />
      {showGoals ? (
        <GoalFilter
          selectedGoalIds={search.goalIds ?? []}
          onGoalIdsChange={(goalIds) => onChange({ goalIds }, { replace: true })}
        />
      ) : null}
      {hasObjectTypes ? (
        <ObjectTypeFilter
          selectedObjectTypeIds={search.objectTypeIds ?? []}
          onObjectTypeIdsChange={(objectTypeIds) => onChange({ objectTypeIds }, { replace: true })}
        />
      ) : null}
    </>
  );
}

/** The subscription topic filter, offered only in a workspace that has topics. */
export function CatalogTopicFilter({ search, onChange }: CatalogFiltersProps) {
  const hasTopics = useHasSubscriptionTopics();
  if (!hasTopics) {
    return null;
  }
  return (
    <TopicFilter
      selectedTopicId={search.subscriptionTopicId == null ? null : String(search.subscriptionTopicId)}
      onTopicChange={(topicId) =>
        onChange({ subscriptionTopicId: topicId == null ? undefined : Number(topicId) }, { replace: false })
      }
    />
  );
}
